package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.remote.api.RemoteCommandApi
import com.iptvplayer.shared.data.remote.dto.AckCommandRequestDto
import com.iptvplayer.shared.data.remote.dto.SendCommandRequestDto
import com.iptvplayer.shared.domain.model.CommandStatus
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.domain.model.RemoteAction
import com.iptvplayer.shared.domain.model.RemoteCommand
import com.iptvplayer.shared.domain.repository.RemoteCommandRepository
import com.iptvplayer.shared.util.Constants
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow

class RemoteCommandRepositoryImpl(
    private val remoteCommandApi: RemoteCommandApi
) : RemoteCommandRepository {

    override suspend fun sendCommand(
        targetDeviceId: String,
        action: RemoteAction,
        contentId: String,
        contentType: ContentType,
        streamUrl: String,
        contentName: String,
        resumePositionMs: Long?
    ): Result<RemoteCommand> = try {
        val dto = remoteCommandApi.sendCommand(
            SendCommandRequestDto(
                targetDeviceId = targetDeviceId,
                action = action.name,
                contentId = contentId,
                contentType = contentType.name,
                streamUrl = streamUrl,
                contentName = contentName,
                resumePositionMs = resumePositionMs
            )
        )
        val command = RemoteCommand(
            id = dto.id,
            fromDeviceId = dto.fromDeviceId,
            targetDeviceId = dto.targetDeviceId,
            action = parseAction(dto.action),
            contentId = dto.contentId,
            contentType = parseContentType(dto.contentType),
            streamUrl = dto.streamUrl,
            contentName = dto.contentName,
            resumePositionMs = dto.resumePositionMs,
            status = parseStatus(dto.status),
            createdAt = dto.createdAt
        )
        Result.Success(command)
    } catch (e: Exception) {
        Result.Error("Failed to send command: ${e.message}", e)
    }

    override fun observePendingCommands(deviceId: String): Flow<RemoteCommand> = flow {
        while (true) {
            try {
                val dtos = remoteCommandApi.getPendingCommands(deviceId)
                for (dto in dtos) {
                    val command = RemoteCommand(
                        id = dto.id,
                        fromDeviceId = dto.fromDeviceId,
                        targetDeviceId = dto.targetDeviceId,
                        action = parseAction(dto.action),
                        contentId = dto.contentId,
                        contentType = parseContentType(dto.contentType),
                        streamUrl = dto.streamUrl,
                        contentName = dto.contentName,
                        resumePositionMs = dto.resumePositionMs,
                        status = parseStatus(dto.status),
                        createdAt = dto.createdAt
                    )
                    emit(command)
                }
            } catch (_: Exception) {
                // Silently ignore polling errors and retry
            }
            delay(Constants.REMOTE_POLL_INTERVAL_MS)
        }
    }

    override suspend fun acknowledgeCommand(
        commandId: String,
        status: CommandStatus
    ): Result<Unit> = try {
        remoteCommandApi.acknowledgeCommand(
            AckCommandRequestDto(
                commandId = commandId,
                status = status.name
            )
        )
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to acknowledge command: ${e.message}", e)
    }

    private fun parseAction(action: String): RemoteAction = try {
        RemoteAction.valueOf(action)
    } catch (_: Exception) {
        RemoteAction.PLAY
    }

    private fun parseContentType(type: String): ContentType = try {
        ContentType.valueOf(type)
    } catch (_: Exception) {
        ContentType.CHANNEL
    }

    private fun parseStatus(status: String): CommandStatus = try {
        CommandStatus.valueOf(status)
    } catch (_: Exception) {
        CommandStatus.PENDING
    }
}
